/**
 * useMutualAid — encapsulates every alliance / shared-call concern that
 * used to bloat Game.jsx (≈ 400 lines).
 *
 * Owns:
 *   - alliance + shares + alliance stations state, plus the 3 s poll
 *   - sharedByCall map (host's local callId → server shareId)
 *   - activeShare (SharedCallDialog open state)
 *   - showAllianceStations / showAllianceUnits map toggles (persisted)
 *   - helpPanelOpen (persisted)
 *   - mutualAidOpen (create/join dialog)
 *
 * Side-effects:
 *   - host: pushes their own dispatches + call state for each share
 *   - host: closes shares automatically when the local call resolves
 *   - helper: recalls helper dispatches when their share goes away
 *   - helper: reconciles on_scene=true server-side if the engine's
 *             single fire-and-forget attempt missed
 *
 * Exposes (returns):
 *   - state: alliance, shares, allianceStations, myShares, sharedCallIds,
 *            activeShare, mutualAidOpen, helpPanelOpen, showAllianceStations,
 *            showAllianceUnits
 *   - actions: refreshAlliance, openShare, closeShareView, setMutualAidOpen,
 *              toggleHelpPanel, toggleAllianceStations, toggleAllianceUnits,
 *              handleShareCall, handleCloseShare,
 *              sendUnitToShare, handleRespondToShare
 *   - selectors: dispatchesWithHelpers(dispatches)
 *   - engine bridges: handleCallUpgraded, handleCallResolved
 *
 * Inputs:
 *   - user
 *   - stateRef          — Game's own ref; read for latest player/stations/calls/etc
 *   - dispatches, setDispatches, setApparatusState, calls
 *   - pushRadio, fireGameEvent
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  myAlliance as apiMyAlliance,
  listShared as apiListShared,
  allianceStations as apiAllianceStations,
  shareCall as apiShareCall,
  resolveShare as apiResolveShare,
  upgradeShare as apiUpgradeShare,
  setRequesterUnits as apiSetRequesterUnits,
  helperDispatch as apiHelperDispatch,
  helperUpdate as apiHelperUpdate,
  closeShare as apiCloseShare,
} from "@/lib/api";
import { EMERGENCY_TYPES, distanceKm } from "@/lib/gameConfig";
import { getRoute, reverseRoute } from "@/lib/routing";
import { nextDispatchId } from "@/hooks/gameIds";

const POLL_INTERVAL_MS = 3000;
const RECONCILE_MAX_ATTEMPTS = 10;

const lsBool = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v !== "false";
  } catch (_e) {
    return fallback;
  }
};
const lsSet = (key, value) => {
  try { localStorage.setItem(key, String(value)); } catch (_e) { /* ignore */ }
};

export default function useMutualAid({
  user,
  stateRef,
  dispatches,
  setDispatches,
  setApparatusState,
  calls,
  setCalls,
  pushRadio,
  fireGameEvent,
  scheduleStaggered,
  nextDispatchAtRef,
}) {
  // ---------- Core state ----------
  const [alliance, setAlliance] = useState(null);
  const [shares, setShares] = useState([]);
  const [allianceStations, setAllianceStations] = useState([]);
  const [mutualAidOpen, setMutualAidOpen] = useState(false);
  const [activeShare, setActiveShare] = useState(null);
  const [sharedByCall, setSharedByCall] = useState({}); // {callId: shareId}

  const [showAllianceStations, setShowAllianceStations] = useState(
    () => lsBool("d911_showAllianceStations", true)
  );
  const [showAllianceUnits, setShowAllianceUnits] = useState(
    () => lsBool("d911_showAllianceUnits", true)
  );
  const [helpPanelOpen, setHelpPanelOpen] = useState(
    () => lsBool("dispatch911_helpPanelOpen", true)
  );

  // Tracks shares the host has already initiated finalization for (either
  // via resolve OR via the auto-close effect). Lifted up here so
  // `handleCallResolved` can claim the share BEFORE its `await
  // apiResolveShare` suspends — otherwise the auto-close effect that runs
  // on the same render (when `calls` drops the resolved call) would race
  // against the resolve and clobber the "resolved" status with "closed",
  // causing helpers to never get paid.
  const closedShareIdsRef = useRef(new Set());

  useEffect(() => lsSet("d911_showAllianceStations", showAllianceStations),
    [showAllianceStations]);
  useEffect(() => lsSet("d911_showAllianceUnits", showAllianceUnits),
    [showAllianceUnits]);

  const toggleHelpPanel = useCallback(() => {
    setHelpPanelOpen((prev) => {
      const next = !prev;
      lsSet("dispatch911_helpPanelOpen", next);
      return next;
    });
  }, []);
  const toggleAllianceStations = useCallback(
    () => setShowAllianceStations((v) => !v), []);
  const toggleAllianceUnits = useCallback(
    () => setShowAllianceUnits((v) => !v), []);

  // ---------- Refresh alliance + shares + alliance stations ----------
  const refreshAlliance = useCallback(async () => {
    try {
      const d = await apiMyAlliance();
      setAlliance(d.alliance || null);
      if (d.alliance) {
        const [stRes, shRes] = await Promise.all([
          apiAllianceStations(d.alliance.id).catch(() => ({ stations: [] })),
          apiListShared(d.alliance.id).catch(() => ({ shares: [] })),
        ]);
        setAllianceStations(stRes.stations || []);
        setShares(shRes.shares || []);
      } else {
        setAllianceStations([]);
        setShares([]);
      }
    } catch (_e) {
      setAlliance(null);
      setAllianceStations([]);
      setShares([]);
    }
  }, []);

  // ---------- Keep ref in sync for game engine reads ----------
  useEffect(() => {
    stateRef.current.shares = shares;
    stateRef.current.userId = user?.user_id;
    stateRef.current.allianceId = alliance?.id || null;
  }, [shares, user?.user_id, alliance?.id, stateRef]);

  // ---------- Keep the open share dialog data fresh ----------
  useEffect(() => {
    if (!activeShare) return;
    const fresh = shares.find((s) => s.id === activeShare.id);
    if (!fresh) { setActiveShare(null); return; }
    if (fresh !== activeShare) setActiveShare(fresh);
  }, [shares, activeShare]);

  // ---------- Helper: recall my helper units when their share is closed/resolved ----------
  useEffect(() => {
    const activeSharedIds = new Set((shares || []).map((s) => s.id));
    const stranded = dispatches.filter(
      (d) => d.helper && d.sharedId && !d.returning && !activeSharedIds.has(d.sharedId)
    );
    if (stranded.length === 0) return;
    const now = Date.now();
    setDispatches((prev) =>
      prev.map((d) => {
        if (!d.helper || !d.sharedId || d.returning) return d;
        if (activeSharedIds.has(d.sharedId)) return d;
        const reversed = (d.routeCoords || []).slice().reverse();
        return {
          ...d,
          returning: true,
          onScene: false,
          callId: null,
          startedAt: now,
          durationSec: d.outboundDurationSec || d.durationSec,
          routeCoords: reversed.length > 1 ? reversed : d.routeCoords,
        };
      })
    );
    stranded.forEach((d) =>
      pushRadio("return", `${d.apparatusName} clear of mutual-aid scene, returning to quarters`)
    );
  }, [shares, dispatches, pushRadio, setDispatches]);

  // ---------- Initial fetch + polling ----------
  useEffect(() => {
    refreshAlliance();
    const id = setInterval(refreshAlliance, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshAlliance]);

  // ---------- Helper on-scene reconciliation ----------
  // If the engine's single fire-and-forget apiHelperUpdate(on_scene=true)
  // dropped, retry until the server agrees. Capped per dispatch.
  const reconcileAttemptsRef = useRef({});
  useEffect(() => {
    if (!alliance) return;
    const sharesById = new Map((shares || []).map((s) => [s.id, s]));
    dispatches.forEach((d) => {
      if (!d.helper || !d.sharedId || !d.onScene || d.returning) return;
      const share = sharesById.get(d.sharedId);
      if (!share) return;
      const h = (share.helpers || []).find(
        (x) => (x.dispatch_id ?? x.dispatchId) === d.id
      );
      if (!h || h.on_scene) return; // already in sync
      const attempts = reconcileAttemptsRef.current[d.id] || 0;
      if (attempts > RECONCILE_MAX_ATTEMPTS) return;
      reconcileAttemptsRef.current[d.id] = attempts + 1;
      apiHelperUpdate(alliance.id, d.sharedId, {
        dispatchId: d.id,
        on_scene: true,
      }).catch(() => {});
    });
  }, [alliance, shares, dispatches]);

  // ---------- Share my call with the alliance ----------
  const handleShareCall = useCallback(async (callId, note) => {
    if (!alliance) {
      toast.error("Join a Mutual Aid Group first");
      return;
    }
    const call = stateRef.current.calls.find((c) => c.id === callId);
    if (!call) return;
    try {
      const t = EMERGENCY_TYPES[call.type];
      const res = await apiShareCall(alliance.id, {
        call: {
          id: call.id,
          type: call.type,
          lat: call.lat,
          lng: call.lng,
          address: call.address || "",
          requiredUnits: call.requiredUnits,
          alternates: call.alternates,
          onSceneDurationSec: call.onSceneDurationSec,
          revealed: !!call.revealed,
          reward: t?.reward || 0, // stashed for server-side auto-resolve when host is offline
          assessSecs: t?.assess_seconds || 30, // server-side patient assessment cadence
        },
        note: note || "",
      });
      if (res?.id) setSharedByCall((m) => ({ ...m, [callId]: res.id }));
      pushRadio("share", `Help requested from ${alliance.name} for ${call.address || "scene"}`);
      refreshAlliance();
    } catch (_e) {
      toast.error("Could not share call");
    }
  }, [alliance, refreshAlliance, pushRadio, stateRef]);

  // ---------- Engine bridges (call upgraded + call resolved) ----------
  const handleCallUpgraded = useCallback(async (callId) => {
    if (!alliance) return;
    const sid = sharedByCall[callId];
    if (!sid) return;
    // Read the LATEST call data (just mutated in the same tick).
    setTimeout(async () => {
      const c = stateRef.current.calls.find((x) => x.id === callId);
      if (!c) return;
      try {
        const ut = EMERGENCY_TYPES[c.type];
        await apiUpgradeShare(alliance.id, sid, {
          call: {
            id: c.id,
            type: c.type,
            lat: c.lat,
            lng: c.lng,
            address: c.address || "",
            requiredUnits: c.requiredUnits,
            alternates: c.alternates,
            onSceneDurationSec: c.onSceneDurationSec,
            revealed: !!c.revealed,
            upgradedFrom: c.upgradedFrom,
            reward: ut?.reward || 0, // refresh reward to match the upgraded type
            assessSecs: ut?.assess_seconds || 30, // server-side patient assessment cadence
          },
        });
      } catch (_e) { /* best-effort */ }
    }, 0);
  }, [alliance, sharedByCall, stateRef]);

  const handleCallResolved = useCallback(async (resolvedCalls) => {
    if (!alliance) return;
    for (const c of resolvedCalls) {
      const sid = sharedByCall[c.id];
      if (!sid) continue;
      // Claim the share BEFORE awaiting the resolve request. This blocks
      // the auto-close effect (further down) from firing `apiCloseShare`
      // in parallel when React re-renders after `setCalls` drops the
      // resolved call. Without this guard, the close request often beat
      // the resolve to the server, and the server-side resolve handler
      // skipped helper payouts because status was already "closed".
      closedShareIdsRef.current.add(sid);
      const t = EMERGENCY_TYPES[c.type];
      const reward = t?.reward || 0;
      try { await apiResolveShare(alliance.id, sid, { reward }); } catch (_e) { /* best-effort */ }
      setSharedByCall((m) => {
        if (!(c.id in m)) return m;
        const nx = { ...m };
        delete nx[c.id];
        return nx;
      });
    }
  }, [alliance, sharedByCall]);

  // ---------- Helper-side: dispatch a unit to a shared call ----------
  const sendUnitToShare = useCallback(async (share, unitId = null) => {
    if (!alliance) return;
    const candidates = [];
    stateRef.current.stations.forEach((s) => {
      (s.trucks || []).forEach((tr) => {
        if (stateRef.current.apparatusState[tr.id]?.available) {
          candidates.push({
            apparatusId: tr.id,
            apparatusName: tr.name,
            apparatusType: tr.type || "Engine",
            station: s,
            d: distanceKm(
              { lat: s.lat, lng: s.lng },
              { lat: share.call.lat, lng: share.call.lng }
            ),
          });
        }
      });
    });
    if (!candidates.length) {
      toast.error("No available units");
      return;
    }
    let pick;
    if (unitId) {
      pick = candidates.find((c) => c.apparatusId === unitId);
      if (!pick) { toast.error("That unit is not available"); return; }
    } else {
      candidates.sort((a, b) => a.d - b.d);
      pick = candidates[0];
    }

    // Reserve the apparatus immediately so duplicate clicks can't grab it.
    // Actual route fetch + dispatch row creation + server sync run inside
    // the shared stagger queue so the helper's truck doesn't start moving
    // simultaneously with their own local dispatches.
    setApparatusState((as) => ({
      ...as,
      [pick.apparatusId]: { ...as[pick.apparatusId], available: false },
    }));

    // Pre-allocate the dispatch id + queuedAt so we can render a placeholder
    // immediately in the helper's CallDialog (showing "QUEUED Xs"), and
    // smoothly upgrade it to a real moving row when the stagger fires.
    const dispatchId = nextDispatchId();
    const queuedAt = Date.now();

    const runOperation = async () => {
      const returningTrip = stateRef.current.dispatches.find(
        (d) => d.apparatusId === pick.apparatusId && d.returning
      );
      const origin = returningTrip
        ? { lat: returningTrip.lat, lng: returningTrip.lng }
        : { lat: pick.station.lat, lng: pick.station.lng };
      if (returningTrip) {
        setDispatches((ds) => ds.filter((d) => d.id !== returningTrip.id));
      }
      const route = await getRoute(origin, { lat: share.call.lat, lng: share.call.lng }, { apparatusType: pick.apparatusType });
      const startedAt = Date.now();
      // Upgrade the placeholder in place (same id) so the row doesn't
      // unmount between QUEUED and EN ROUTE states.
      setDispatches((ds) =>
        ds.map((x) =>
          x.id === dispatchId
            ? {
                ...x,
                queued: false,
                queuedAt: undefined,
                queuedUntil: undefined,
                routeCoords: route.coords,
                lat: route.coords[0][0],
                lng: route.coords[0][1],
                toLat: share.call.lat,
                toLng: share.call.lng,
                durationSec: route.durationSec,
                outboundDurationSec: route.durationSec,
                startedAt,
              }
            : x
        )
      );
      try {
        await apiHelperDispatch(alliance.id, share.id, {
          apparatusName: pick.apparatusName,
          apparatusType: pick.apparatusType,
          origin,
          routeCoords: route.coords,
          durationSec: route.durationSec,
          startedAt,
          dispatchId,
        });
        pushRadio(
          "dispatch",
          returningTrip
            ? `${pick.apparatusName} diverted to mutual-aid call from ${share.requester_name}`
            : `${pick.apparatusName} responding to mutual-aid call from ${share.requester_name}`
        );
        fireGameEvent("mutual_aid_responses", 1);
        refreshAlliance();
      } catch (_e) {
        toast.error("Could not send unit");
      }
    };

    // Queue the operation and learn the delay so the placeholder knows
    // when it'll transition to EN ROUTE.
    const delay = scheduleStaggered && nextDispatchAtRef
      ? scheduleStaggered(nextDispatchAtRef, runOperation)
      : 0;
    if (!scheduleStaggered) runOperation();

    // Insert the placeholder row right after scheduling so we have `delay`.
    setDispatches((ds) => [
      ...ds,
      {
        id: dispatchId,
        apparatusId: pick.apparatusId,
        apparatusName: pick.apparatusName,
        apparatusType: pick.apparatusType,
        stationId: pick.station.id,
        callId: null,
        sharedId: share.id,
        lat: pick.station.lat,
        lng: pick.station.lng,
        toLat: share.call.lat,
        toLng: share.call.lng,
        routeCoords: [[pick.station.lat, pick.station.lng], [share.call.lat, share.call.lng]],
        durationSec: 60,
        startedAt: queuedAt + delay,
        onScene: false,
        returning: false,
        helper: true,
        queued: delay > 0,
        queuedAt,
        queuedUntil: queuedAt + delay,
      },
    ]);
  }, [alliance, refreshAlliance, fireGameEvent, pushRadio,
      setApparatusState, setDispatches, stateRef, scheduleStaggered, nextDispatchAtRef]);

  const handleRespondToShare = useCallback(
    (share) => sendUnitToShare(share, null),
    [sendUnitToShare]
  );

  // ---------- Requester closes their share manually ----------
  const handleCloseShare = useCallback(async (sid) => {
    if (!alliance) return;
    try {
      await apiCloseShare(alliance.id, sid);
      refreshAlliance();
    } catch (_e) { /* swallow */ }
  }, [alliance, refreshAlliance]);

  // ---------- Auto-close my share when its underlying call resolves locally ----------
  useEffect(() => {
    if (!alliance) return;
    const liveCallIds = new Set(calls.map((c) => c.id));
    shares.forEach((s) => {
      if (s.requester_id !== user?.user_id) return;
      if (s.call?.id == null) return;
      if (liveCallIds.has(s.call.id)) return;
      if (closedShareIdsRef.current.has(s.id)) return;
      closedShareIdsRef.current.add(s.id);
      apiCloseShare(alliance.id, s.id).then(refreshAlliance).catch(() => {});
    });
  }, [calls, shares, alliance, user?.user_id, refreshAlliance]);

  // ---------- Helper-side: route my truck home when the share goes away ----------
  const helperRecalledRef = useRef(new Set());
  useEffect(() => {
    if (!alliance) return;
    const openShareIds = new Set(shares.map((s) => s.id));
    const myDispatches = stateRef.current.dispatches;
    const stationsNow = stateRef.current.stations;
    const toRecall = myDispatches.filter(
      (d) => d.helper && d.sharedId && !d.returning && !openShareIds.has(d.sharedId)
    );
    if (!toRecall.length) return;
    setDispatches((ds) =>
      ds.map((d) => {
        if (!toRecall.find((r) => r.id === d.id)) return d;
        if (helperRecalledRef.current.has(d.id)) return d;
        helperRecalledRef.current.add(d.id);
        const st = stationsNow.find((s) => s.id === d.stationId);
        if (!st) return d;
        const reversed = reverseRoute({ coords: d.routeCoords });
        const dur = d.outboundDurationSec || d.durationSec;
        return {
          ...d,
          callId: null,
          sharedId: null,
          onScene: false,
          returning: true,
          routeCoords: reversed.coords,
          startedAt: Date.now(),
          durationSec: dur,
        };
      })
    );
  }, [shares, alliance, setDispatches, stateRef]);

  // ---------- Derived state ----------
  const myShares = useMemo(
    () => shares.filter((s) => s.requester_id === user?.user_id),
    [shares, user?.user_id]
  );
  const sharedCallIds = useMemo(
    () => new Set(myShares.map((s) => s.call?.id).filter((id) => id != null)),
    [myShares]
  );

  // Helper-pseudo-dispatches for the requester's CallDialog (virtual rows
  // showing alliance helpers' units).
  const dispatchesWithHelpers = useMemo(() => {
    if (!myShares.length) return dispatches;
    const extras = [];
    myShares.forEach((s) => {
      const cid = s.call?.id;
      if (cid == null) return;
      (s.helpers || []).forEach((h, idx) => {
        extras.push({
          id: `share-helper-${s.id}-${idx}`,
          callId: cid,
          apparatusId: `share-${s.id}-${idx}`,
          apparatusName: `${h.apparatusName} (${h.name})`,
          apparatusType: h.apparatusType || "Engine",
          stationId: null,
          startedAt: h.startedAt || Date.now() - 1000,
          durationSec: h.durationSec || 30,
          onScene: !!h.on_scene,
          returning: !!h.returning,
          helper: true,
          external: true,
        });
      });
    });
    return extras.length ? [...dispatches, ...extras] : dispatches;
  }, [dispatches, myShares]);

  // ---------- Push my units (+call state +patients) to each share I'm hosting ----------
  // So HELPERS' SharedCallDialog can see the FULL combined response (not just
  // helper units), AND server-side auto-resolve can gate on patient state.
  // Cached by serialized snapshot to avoid spammy writes.
  const lastSyncedRef = useRef({});
  useEffect(() => {
    if (!alliance || !myShares.length) return;
    myShares.forEach((s) => {
      const callId = s.call?.id;
      if (callId == null) return;
      const units = dispatches
        .filter((d) => d.callId === callId && !d.returning)
        .map((d) => ({
          dispatch_id: d.id,
          apparatusName: d.apparatusName,
          apparatusType: d.apparatusType || "Engine",
          on_scene: !!d.onScene,
          returning: !!d.returning,
          startedAt: d.startedAt,
          durationSec: d.durationSec,
        }));
      const liveCall = calls.find((c) => c.id === callId);
      const state = liveCall?.state || "pending";
      const onSceneStartedAt = liveCall?.onSceneStartedAt || null;
      // Strip patients down to the fields the server / helpers need.
      // `assessProgress` is quantized to 5% so we only resync on meaningful
      // bumps (≈20 writes max per assessment) — gives helpers a live-ish
      // progress bar without slamming the server every 100ms tick.
      const patients = (liveCall?.patients || []).map((p) => ({
        id: p.id,
        assessed: !!p.assessed,
        needsTransport: !!p.needsTransport,
        specialty: p.specialty || null,
        transportStatus: p.transportStatus || "none",
        destinationHospitalId: p.destinationHospitalId || null,
        assessProgress: Math.round(((p.assessProgress || 0) * 20)) / 20, // round to 0.05
      }));
      const key = JSON.stringify({ units, state, onSceneStartedAt, patients });
      if (lastSyncedRef.current[s.id] === key) return;
      lastSyncedRef.current[s.id] = key;
      apiSetRequesterUnits(alliance.id, s.id, units, {
        state,
        onSceneStartedAt,
        patients,
      }).catch(() => {
        delete lastSyncedRef.current[s.id];
      });
    });
  }, [alliance, myShares, dispatches, calls]);

  // ---------- Pull patient updates from share docs into host's local calls ----------
  // The server stamps `transportStatus` transitions (in_transit, delivered)
  // when helpers post `/patient-transport` OR when the auto-deliver sweep
  // fires. Reflect those back into `call.patients` so the host's local engine
  // counts them as settled (= allows the call to close).
  const lastSharePatientsRef = useRef({});
  useEffect(() => {
    if (!alliance || !myShares.length) return;
    let mutated = false;
    const byCallId = new Map();
    myShares.forEach((s) => {
      const cid = s.call?.id;
      const sp = s.patients || [];
      if (cid == null || sp.length === 0) return;
      // Build map of patient.id → server status info
      const map = {};
      sp.forEach((p) => { if (p.id) map[p.id] = p; });
      // Compare to last-seen — skip if nothing changed for this share.
      const sig = JSON.stringify(sp.map((p) => [p.id, p.transportStatus, p.destinationHospitalId]));
      if (lastSharePatientsRef.current[s.id] === sig) return;
      lastSharePatientsRef.current[s.id] = sig;
      byCallId.set(cid, map);
      mutated = true;
    });
    if (!mutated || !setCalls) return;
    setCalls((prev) =>
      prev.map((c) => {
        const m = byCallId.get(c.id);
        if (!m || !Array.isArray(c.patients) || c.patients.length === 0) return c;
        let any = false;
        const next = c.patients.map((p) => {
          const serverP = m[p.id];
          if (!serverP) return p;
          const serverStatus = serverP.transportStatus || "none";
          // Only ADVANCE the state: none → in_transit → delivered. Never
          // regress (the local engine may briefly be ahead of the server).
          const advanceMap = { none: 0, queued: 1, in_transit: 2, refused: 3, delivered: 4 };
          const localRank = advanceMap[p.transportStatus || "none"] ?? 0;
          const serverRank = advanceMap[serverStatus] ?? 0;
          if (serverRank <= localRank) return p;
          any = true;
          return {
            ...p,
            transportStatus: serverStatus,
            destinationHospitalId: serverP.destinationHospitalId || p.destinationHospitalId || null,
            deliveredHospital: serverP.delivered_hospital_id ? (serverP.destinationHospitalName || p.deliveredHospital || "hospital") : p.deliveredHospital,
          };
        });
        return any ? { ...c, patients: next } : c;
      })
    );
  }, [alliance, myShares, setCalls]);

  // ---------- Stable open / close-view helpers ----------
  const openShare = useCallback((share) => setActiveShare(share), []);
  const closeShareView = useCallback(() => setActiveShare(null), []);

  return {
    // state
    alliance,
    shares,
    allianceStations,
    myShares,
    sharedCallIds,
    activeShare,
    mutualAidOpen,
    helpPanelOpen,
    showAllianceStations,
    showAllianceUnits,

    // selectors
    dispatchesWithHelpers,

    // actions
    refreshAlliance,
    openShare,
    closeShareView,
    setMutualAidOpen,
    toggleHelpPanel,
    toggleAllianceStations,
    toggleAllianceUnits,
    handleShareCall,
    handleCloseShare,
    sendUnitToShare,
    handleRespondToShare,

    // engine bridges (assigned to refs in Game.jsx)
    handleCallUpgraded,
    handleCallResolved,
  };
}
